import { useState } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import MedicaoMoldagemPDF from '../components/MedicaoMoldagemPDF';
import MedicaoRupturaPDF from '../components/MedicaoRupturaPDF';
import { LOGO_EXEMPLO, ASSINATURA_EXEMPLO } from '../components/pdf/exemploImagens';

// Página de desenvolvimento: mostra os PDFs de "Medição" (moldagem e ruptura) ao vivo, sem backend.
// Acesse em /app/dev-pdf-preview-medicao (moldagem) ou /app/dev-pdf-preview-medicao-ruptura. Não aparece no menu.
// Cobre: dias de semana/sábado/domingo no histórico, vários Fck e idades, CPs rompidos e previstos
// (sem carga/Fcj) e quebra de página com muitos CPs.

const empresaMock = 'Construtora Exemplo Ltda';
const obraMock = 'Edifício Monte Líbano';
const periodoMock = '17/08/2026 a 21/09/2026';

// Dias de concretagem de exemplo (inclui sábado 22/08 e domingo 23/08).
const diasMock = ['2026-08-18', '2026-08-19', '2026-08-21', '2026-08-22', '2026-08-23', '2026-08-27', '2026-09-01', '2026-09-03'];
const FCKS = [25, 35, 50];
const IDADES = [7, 28, 63];

const moldagemCps = diasMock.flatMap((data, d) =>
  Array.from({ length: 8 }, (_, i) => ({
    idCorpoProva: d * 100 + i,
    etiqueta: String(1000 + d * 10 + i),
    idadeRuptura: IDADES[i % 3],
    dataRuptura: data,
    idCaminhao: d * 10 + Math.floor(i / 4),
    numrNotaFiscalPrincipal: 54000 + d * 10 + Math.floor(i / 4),
    numrNotaFiscalSecundaria: null,
    fck: FCKS[d % 3],
    dataConcretagem: `${data}T00:00:00`,
    tipoCorpoProva: 'Cilíndrico 10x20',
  }))
);

const moldagemResumo = {
  totalCaminhoes: diasMock.length * 2,
  totalCps: moldagemCps.length,
  cpsSabado: 8,
  cpsDomingo: 8,
  porDia: diasMock.map((data) => ({
    data,
    diaSemana: new Date(`${data}T12:00:00`).getDay(),
    qtdCaminhao: 2,
    qtdCp: 8,
  })),
};

const rupturaCps = Array.from({ length: 40 }, (_, i) => {
  const rompido = i % 4 !== 0;
  return {
    idCorpoProva: i,
    etiqueta: String(2000 + i),
    idadeRuptura: IDADES[i % 3],
    fck: FCKS[i % 3],
    dataConcretagem: '2026-08-10T00:00:00',
    dataRuptura: `2026-09-${String(1 + Math.floor(i / 2)).padStart(2, '0')}T00:00:00`,
    status: rompido ? 'rompido' : 'previsto',
    valrCarga: rompido ? 20 + (i % 7) : null,
    fcj: rompido ? 28.4 + (i % 9) : null,
    tipoRuptura: rompido ? 'A' : null,
    prensa: rompido ? 'P-01' : null,
  };
});

const agrupar = (campo) => {
  const mapa = new Map();
  rupturaCps.forEach((cp) => {
    const g = mapa.get(cp[campo]) ?? { qtd: 0, rompidos: 0, previstos: 0 };
    g.qtd += 1;
    if (cp.status === 'rompido') g.rompidos += 1;
    else g.previstos += 1;
    mapa.set(cp[campo], g);
  });
  return [...mapa.entries()].sort((a, b) => a[0] - b[0]);
};

const rupturaResumo = {
  total: rupturaCps.length,
  rompidos: rupturaCps.filter((c) => c.status === 'rompido').length,
  previstos: rupturaCps.filter((c) => c.status === 'previsto').length,
  porFck: agrupar('fck').map(([fck, g]) => ({ fck, ...g })),
  porIdade: agrupar('idadeRuptura').map(([idade, g]) => ({ idade, ...g })),
};

function DevPdfPreviewMedicaoPage({ tipoInicial = 'moldagem' }) {
  const [tipo, setTipo] = useState(tipoInicial);
  const [comImagens, setComImagens] = useState(true);

  const comuns = {
    empresaNome: empresaMock,
    obraNome: obraMock,
    periodo: periodoMock,
    logoCliente: comImagens ? LOGO_EXEMPLO : null,
    assinaturas: comImagens ? [{ src: ASSINATURA_EXEMPLO, nome: 'Eng. Fulano de Tal' }, { src: ASSINATURA_EXEMPLO, nome: 'Eng. Beltrano da Silva' }] : [],
  };

  return (
    <div style={{ height: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem' }}>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="moldagem">Medição - Moldagem</option>
          <option value="ruptura">Medição - Ruptura</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" checked={comImagens} onChange={(e) => setComImagens(e.target.checked)} />
          Com logo/assinaturas de exemplo
        </label>
      </div>
      <div style={{ flex: 1 }}>
        <PDFViewer key={`${tipo}-${comImagens}`} width="100%" height="100%" showToolbar>
          {tipo === 'moldagem' ? (
            <MedicaoMoldagemPDF resumo={moldagemResumo} cps={moldagemCps} {...comuns} />
          ) : (
            <MedicaoRupturaPDF resumo={rupturaResumo} cps={rupturaCps} {...comuns} />
          )}
        </PDFViewer>
      </div>
    </div>
  );
}

export default DevPdfPreviewMedicaoPage;
